import { useAuth } from '@opendoor/auth-fe/context';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { useTimeBoundedFeatureFlagEvaluator } from 'components/helpers/hooks';

/**
 * Kill switch for the in-browser tour booking flow. Off or unresolved keeps the
 * app-handoff modal that predates it.
 */
export const WEB_TOUR_BOOKING_FLAG = 'buyer-web-tour-booking';

const FLAG_READ_STALE_MS = 5 * 60 * 1000;

export type WebTourBookingGate = { isResolved: false } | { isResolved: true; isEnabled: boolean };

export function useWebTourBookingGate(): WebTourBookingGate {
  const { authentication } = useAuth();
  const customerUuid =
    authentication.state === 'authenticated' ? (authentication.user.customerUuid ?? '') : '';
  const evaluateFlag = useTimeBoundedFeatureFlagEvaluator();
  const query = useQuery(
    queryOptions({
      queryKey: ['tours', 'flag', WEB_TOUR_BOOKING_FLAG, customerUuid] as const,
      queryFn: () => evaluateFlag(WEB_TOUR_BOOKING_FLAG),
      retry: false,
      refetchOnWindowFocus: false,
      // The evaluator's own deadline resolves flag-off on a hung BFF, so a resolved false can
      // be a transient failure rather than a real read. A finite staleTime lets that
      // resolution self-heal on a later visit instead of sticking for the whole session; a
      // thrown read is never cached at all and retries on the next mount.
      staleTime: FLAG_READ_STALE_MS,
    }),
  );
  return query.data === undefined
    ? { isResolved: false }
    : { isResolved: true, isEnabled: query.data };
}
